import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Alert
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { DraggableDialog } from '@/shared/components/DraggableDialog';
import { useSectionStyles } from '@/styles/utilities/hooks';
import InputsBrowserAlert from '../components/InputsBrowserAlert';

export default function InputsBrowser({
  allFMOInputs,
  allZipfiles,
  selectedNode,
  onNodeSelect,
  filteredZipfileCount,
  loading,
  showHelp,
  onHelpToggle
}) {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  const [expandedItems, setExpandedItems] = useState([]);

  // Build tree structure from FMO inputs data
  const treeData = useMemo(() => {
    if (!allFMOInputs || allFMOInputs.length === 0) return {};
    
    const structure = {};
    
    // Create a map of FMO input IDs to zipfile IDs and their tree row IDs (using id_ field)
    const fmoInputToZipfilesMap = new Map();
    const zipfileTreeRowsMap = new Map(); // Map zipfile ID to all its id_ values (parent and spec rows)
    
    if (allZipfiles) {
      allZipfiles.forEach(zipfile => {
        const zipfileId = zipfile.id_;
        const treeRowIds = new Set();
        
        // Add parent row ID (using id_ field)
        treeRowIds.add(String(zipfileId));
        
        // Add child spec row IDs if FMO inputs are available (using id_ field format)
        if (zipfile.entry_ids && allFMOInputs.length > 0) {
          // Get FMO inputs for this zipfile to determine spec groups
          const zipfileFMOInputs = allFMOInputs.filter(fmoInput => 
            zipfile.entry_ids.includes(fmoInput.id_)
          );
          
          // Group by spec combination to determine child row IDs
          const specGroups = {};
          zipfileFMOInputs.forEach(fmoInput => {
            const clusterspec = fmoInput.clusterspec?.name || 'Unknown';
            const pocketspec = fmoInput.pocketspec?.name || 'Unknown';
            const inputspec = fmoInput.inputspec?.name || 'Unknown';
            const specKey = `${clusterspec}|${pocketspec}|${inputspec}`;
            if (!specGroups[specKey]) {
              specGroups[specKey] = true;
            }
          });
          
          // Add child row IDs using id_ format
          Object.keys(specGroups).forEach((_, index) => {
            treeRowIds.add(`${zipfileId}-${index + 1}`);
          });
        }
        
        // Store the complete set of id_ values for this zipfile
        zipfileTreeRowsMap.set(zipfileId, treeRowIds);
        
        // Map entries to zipfiles
        if (zipfile.entry_ids) {
          zipfile.entry_ids.forEach(fmoInputId => {
            if (!fmoInputToZipfilesMap.has(fmoInputId)) {
              fmoInputToZipfilesMap.set(fmoInputId, new Set());
            }
            fmoInputToZipfilesMap.get(fmoInputId).add(zipfileId);
          });
        }
      });
    }
    
    allFMOInputs.forEach(fmoInput => {
      if (!fmoInput.prep) return;
      
      const { project, target } = fmoInput.prep;
      // Handle empty batch as '<none>'
      const batch = fmoInput.prep.batch || '<none>';
      
      if (!project) return;
      
      // Initialize project level
      if (!structure[project]) {
        structure[project] = {
          id: `project-${project}`,
          label: project,
          type: 'project',
          name: project,
          targets: {}
        };
      }
      
      if (!target) return;
      
      // Initialize target level
      if (!structure[project].targets[target]) {
        structure[project].targets[target] = {
          id: `target-${project}-${target}`,
          label: target,
          type: 'target',
          name: target,
          project: project,
          batches: {}
        };
      }
      
      // Initialize batch level
      const batchKey = batch;
      if (!structure[project].targets[target].batches[batchKey]) {
        structure[project].targets[target].batches[batchKey] = {
          id: `batch-${project}-${target}-${batchKey}`,
          label: batch,
          type: 'batch',
          name: batch,
          project: project,
          target: target,
          items: [],
          zipfileIds: new Set(),
          treeRowIds: new Set(),  // Add treeRowIds for filtering
          earliestDate: null
        };
      }
      
      const batchNode = structure[project].targets[target].batches[batchKey];
      batchNode.items.push(fmoInput.id_);
      
      // Add zipfile IDs and their tree row IDs for this entry
      if (fmoInputToZipfilesMap.has(fmoInput.id_)) {
        fmoInputToZipfilesMap.get(fmoInput.id_).forEach(zipfileId => {
          batchNode.zipfileIds.add(zipfileId);
          // Add all tree row IDs for this zipfile
          if (zipfileTreeRowsMap.has(zipfileId)) {
            zipfileTreeRowsMap.get(zipfileId).forEach(rowId => {
              batchNode.treeRowIds.add(rowId);
            });
          }
        });
      }
      
      // Track earliest date for all batches (including <none>)
      if (fmoInput.prep.prepared_at) {
        if (!batchNode.earliestDate || fmoInput.prep.prepared_at < batchNode.earliestDate) {
          batchNode.earliestDate = fmoInput.prep.prepared_at;
        }
      }
    });
    
    // Aggregate treeRowIds for target and project levels
    Object.values(structure).forEach(project => {
      // Initialize project treeRowIds
      project.zipfileIds = new Set();
      project.treeRowIds = new Set();
      
      Object.values(project.targets).forEach(target => {
        // Initialize target treeRowIds
        target.zipfileIds = new Set();
        target.treeRowIds = new Set();
        
        // Aggregate from batches to target
        Object.values(target.batches).forEach(batch => {
          batch.zipfileIds.forEach(id => target.zipfileIds.add(id));
          batch.treeRowIds.forEach(id => target.treeRowIds.add(id));
        });
        
        // Aggregate from target to project
        target.zipfileIds.forEach(id => project.zipfileIds.add(id));
        target.treeRowIds.forEach(id => project.treeRowIds.add(id));
      });
    });
    
    return structure;
  }, [allFMOInputs, allZipfiles]);
  
  // Don't auto-expand any nodes - start with tree fully collapsed
  
  // Handle clear filter
  const handleClearFilter = useCallback(() => {
    onNodeSelect(null);
  }, [onNodeSelect]);
  
  // Render tree items recursively
  const renderTreeItems = (projects) => {
    // Sort projects alphabetically
    const sortedProjects = Object.values(projects).sort((a, b) => a.label.localeCompare(b.label));
    
    return sortedProjects.map((project) => {
      // Sort targets alphabetically
      const sortedTargets = Object.values(project.targets).sort((a, b) => a.label.localeCompare(b.label));
      
      return (
        <TreeItem
          key={project.id}
          itemId={project.id}
          label={project.label}
        >
          {sortedTargets.map((target) => {
            // Sort batches by date first, then alphabetically
            const sortedBatches = Object.values(target.batches).sort((a, b) => {
              // Sort by earliest date first
              if (a.earliestDate && b.earliestDate) {
                const dateCompare = a.earliestDate.localeCompare(b.earliestDate);
                if (dateCompare !== 0) return dateCompare;
              } else if (a.earliestDate) {
                return -1; // a has date, b doesn't - a comes first
              } else if (b.earliestDate) {
                return 1; // b has date, a doesn't - b comes first
              }
              // If dates are same or both missing, sort by label
              return a.label.localeCompare(b.label);
            });
            
            return (
              <TreeItem
                key={target.id}
                itemId={target.id}
                label={target.label}
              >
                {sortedBatches.map((batch) => {
                  const zipfileCount = batch.zipfileIds.size;
                  let batchLabel;
                  
                  if (batch.name === '<none>') {
                    if (batch.earliestDate) {
                      const date = new Date(batch.earliestDate);
                      const yy = date.getFullYear().toString().slice(-2);
                      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
                      const dd = date.getDate().toString().padStart(2, '0');
                      batchLabel = `${yy}${mm}${dd} <none> (${zipfileCount})`;
                    } else {
                      batchLabel = `<none> (${zipfileCount})`;
                    }
                  } else if (batch.earliestDate) {
                    const date = new Date(batch.earliestDate);
                    const yy = date.getFullYear().toString().slice(-2);
                    const mm = (date.getMonth() + 1).toString().padStart(2, '0');
                    const dd = date.getDate().toString().padStart(2, '0');
                    batchLabel = `${yy}${mm}${dd} ${batch.label} (${zipfileCount})`;
                  } else {
                    batchLabel = `${batch.label} (${zipfileCount})`;
                  }
                  
                  return (
                    <TreeItem
                      key={batch.id}
                      itemId={batch.id}
                      label={batchLabel}
                    />
                  );
                })}
              </TreeItem>
            );
          })}
        </TreeItem>
      );
    });
  };
  
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Help Dialog */}
      <DraggableDialog
        open={showHelp}
        onClose={() => onHelpToggle(false)}
        title="Browser Help - Inputs Tab"
        variant="help"
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{
          '& h3': { fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, mt: theme.spacing(2), mb: theme.spacing(1) },
          '& h3:first-of-type': { mt: 0 },
          '& ul': { pl: theme.spacing(2), my: theme.spacing(0.5) },
          '& li': { fontSize: theme.typography.fontSize.xs, mb: theme.spacing(0.5) },
          '& code': {
            backgroundColor: theme.palette.grey[200],
            px: theme.spacing(0.5),
            py: theme.spacing(0.2),
            borderRadius: theme.spacing(0.5),
            fontSize: theme.typography.fontSize.xs,
            fontFamily: 'monospace'
          },
          '& p': { fontSize: theme.typography.fontSize.xs, my: theme.spacing(0.5) },
          '& strong': { fontWeight: theme.typography.fontWeight.semibold }
        }}>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
{`### 1. Tree Hierarchy
The Browser organizes input archives in three levels:
- **Project** — Top-level grouping
  - **Target** — Protein target within the project
    - **Batch** — Collection of related input archives
      - Format: \`YYMMDD batch_name (count)\`
      - Number shows archive count in this batch

### 2. Navigate and Filter
- Click any tree node to filter the table
- The main table shows archives from selected node
- Click \`Clear\` to reset and show all archives

### 3. Archive Details
Each archive contains:
- Multiple FMO input files (entries)
- Different specification combinations
- Ready for computational processing`}
          </ReactMarkdown>
        </Box>
      </DraggableDialog>
      {/* Always show the alert panel */}
      <InputsBrowserAlert
        selectedNode={selectedNode}
        filteredZipfileCount={filteredZipfileCount}
        totalZipfileCount={allZipfiles?.length || 0}
        onClearFilter={handleClearFilter}
      />
      
      {/* Tree View Section */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        px: theme.spacing(2),
        pb: theme.spacing(2)
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(4)
          }}>
            <Typography sx={{ 
              fontSize: theme.typography.fontSize.sm,
              color: 'text.secondary'
            }}>
              Loading FMO inputs...
            </Typography>
          </Box>
        ) : Object.keys(treeData).length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            No FMO inputs found
          </Alert>
        ) : (
          <SimpleTreeView
            selectedItems={selectedNode?.id || null}
            onSelectedItemsChange={(event, itemId) => {
              // Find the node data from treeData
              let node = null;
              
              // Search for the node in the tree structure
              Object.values(treeData).forEach(project => {
                if (project.id === itemId) {
                  node = project;
                } else {
                  Object.values(project.targets).forEach(target => {
                    if (target.id === itemId) {
                      node = target;
                    } else {
                      Object.values(target.batches).forEach(batch => {
                        if (batch.id === itemId) {
                          node = batch;
                        }
                      });
                    }
                  });
                }
              });
              
              onNodeSelect(node);
            }}
            expandedItems={expandedItems}
            onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
            sx={{
              '& .MuiTreeItem-label': {
                fontSize: theme.typography.fontSize?.base || '0.875rem'
              }
            }}
          >
            {renderTreeItems(treeData)}
          </SimpleTreeView>
        )}
      </Box>
    </Box>
  );
}